import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { ToolsInterface } from "../../common/interfaces";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  ToolsConditionStatusLabel,
  TypeToolsConditionStatus
} from "../enum/toolsStatusEnum";
import PageToaster from "../toaster/PageToaster";

interface ToolsCreateModalProps {
  open: boolean;
  onClose: () => void;
  tools?: ToolsInterface | null;
}

const ToolsCreateModal: React.FC<ToolsCreateModalProps> = ({
  open,
  onClose,
  tools
}) => {
  const { isUser } = useAuth();

  const [toolsName, setToolsName] = useState<string>("");
  const [toolsPartNumber, setToolsPartNumber] = useState<string>("");
  const [toolsHeritage, setToolsHeritage] = useState<string>("");
  const [toolsCondition, setToolsCondition] =
    useState<TypeToolsConditionStatus>(TypeToolsConditionStatus.NONE);
  const [toolsBrand, setToolsBrand] = useState<string>("");
  const [toolsModel, setToolsModel] = useState<string>("");

  const [isUpdate, setIsUpdate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const containerId = "toaster-tools-create-modal";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  // Pré‐preencher campos para edição
  useEffect(() => {
    if (tools) {
      setIsUpdate(true);
      setToolsCondition(tools.tools_condition!);
      setToolsName(tools.tools_name || "");
      setToolsPartNumber(tools.tools_part_number || "");
      setToolsHeritage(tools.tools_heritage_number || "");
      setToolsBrand(tools.tools_brand || "");
      setToolsModel(tools.tools_model || "");
    } else {
      clear();
    }
  }, [tools]);

  const clear = () => {
    setIsUpdate(false);
    setToolsCondition(TypeToolsConditionStatus.NONE);
    setToolsName("");
    setToolsPartNumber("");
    setToolsHeritage("");
    setToolsBrand("");
    setToolsModel("");
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const payload = {
      tools_name: toolsName,
      tools_part_number: toolsPartNumber,
      tools_heritage_number: toolsHeritage,
      tools_condition: toolsCondition,
      tools_brand: toolsBrand,
      tools_model: toolsModel
    };

    try {
      if (isUpdate && tools) {
        await api.put(`/tools/${tools.tools_id}`, payload);
        autoSuccess("Ativo atualizado com sucesso!");
      } else {
        console.log("payload", payload);
        await api.post("/tools", payload);
        autoSuccess("Ativo criado com sucesso!");
      }
      clear();
      onClose();
    } catch (err: any) {
      console.error("Erro ao salvar ativo:", err.response?.data || err);
      handleHttpError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={onClose} aria-labelledby="modal-tools">
        <Box
          sx={{
            position: "absolute" as const,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            width: { xs: "90vw", sm: "500px" },
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              {isUpdate ? "Editar Ativo" : "Novo Ativo"}
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            label="Nome do Ativo"
            value={toolsName}
            onChange={(e) => setToolsName(e.target.value)}
            margin="normal"
            multiline
            minRows={1}
          />

          <TextField
            fullWidth
            label="Part Number"
            value={toolsPartNumber}
            onChange={(e) => setToolsPartNumber(e.target.value)}
            margin="normal"
            multiline
            minRows={1}
          />

          <TextField
            fullWidth
            label="Numero de Patrimônio"
            value={toolsHeritage}
            onChange={(e) => setToolsHeritage(e.target.value)}
            margin="normal"
            multiline
            minRows={1}
          />

          <TextField
            fullWidth
            label="Marca"
            value={toolsBrand}
            onChange={(e) => setToolsBrand(e.target.value)}
            margin="normal"
            multiline
            minRows={1}
          />

          <TextField
            fullWidth
            label="Modelo"
            value={toolsModel}
            onChange={(e) => setToolsModel(e.target.value)}
            margin="normal"
            multiline
            minRows={1}
          />

          {isUpdate && tools && (
            <FormControl fullWidth margin="normal">
              <InputLabel
                sx={{ color: "#04d", fontWeight: "600" }}
                id="label-status"
              >
                Status
              </InputLabel>
              <Select
                sx={{ boxShadow: "0 3px 6px rgba(10, 85, 128, 0.81)" }}
                labelId="label-status"
                value={toolsCondition}
                label="Status"
                onChange={(e) =>
                  setToolsCondition(e.target.value as TypeToolsConditionStatus)
                }
              >
                {(
                  Object.values(
                    TypeToolsConditionStatus
                  ) as TypeToolsConditionStatus[]
                )
                  .filter((code) => code !== TypeToolsConditionStatus.NONE)
                  .map((statusCode) => (
                    <MenuItem key={statusCode} value={statusCode}>
                      {ToolsConditionStatusLabel[statusCode]}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          {/* Botões de ação */}
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button onClick={onClose} color="secondary" sx={{ mr: 2 }}>
              Cancelar
            </Button>
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              {isUpdate ? "Atualizar" : "Cadastrar"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ToolsCreateModal;
